import React, { Component } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';

import { CgDetailsMore } from 'react-icons/cg';
import { LuCheck, LuChevronDown, LuChevronUp, LuCircle, LuCopy, LuFile, LuHelpCircle, LuInfo,
         LuMessageSquare, LuReply } from 'react-icons/lu';

import Clipboard from 'blob_types/blobhub/compute/workflow/controls/common/clipboard';
import Tooltip from 'blob_types/blobhub/compute/workflow/controls/common/tooltip';
import Utils from 'context/utils';
import StatusPill from '../../components/statusPill';
import ThreadItemAction from '../../../common/threadItemAction';
import WidgetStatus from '../../../common/widgetStatus';
import { MarkdownBody } from '../../../common/markdown';
import { observeNarrow } from '../../../common/narrowWidth';
import ThreadView from '../../../common/threadView';
import { copyText } from '../../../common/threadItems';
import AceView from '../../../common/aceView';
import { ThreadImage, FileChip, isImageBlock } from '../../../common/fileBlocks';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-sh';
import 'ace-builds/src-noconflict/mode-diff';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-xml';
import CellInput, { useAnswerDraft } from './cellInput';
import InputControl from './inputControl';
import { answersByQuestion, artifactMeta, artifactText, fileContextFor, isFileArtifact,
         openQuestionId } from '../../domain/cellObjects';
import { describeItem, describeRecord, formatSize, formatUsage } from '../../domain/present';
import { isSingleExitLabel } from '../../domain/schema';

/**
 * The panes, built on the playground THREAD widget's own row/bubble vocabulary
 * (`playground-thread-row` / `-bubble` / `-meta`) rather than a private one. Two threads rendered
 * two ways inside one product is a worse outcome than any styling detail, and the widget already
 * solved self-vs-other sides, relative time with an absolute tooltip, and markdown bodies.
 *
 * What is NOT borrowed is the composer: this surface writes through the cell's own controls, which
 * carry the routing semantics a bare text box cannot.
 */

const CLAMP_AT = 240;

/**
 * A row: the bubble, then the thread widget's meta line — time, and the item's actions.
 *
 * The expand toggle lives in that action bar rather than inside the bubble. Inside, it read as part
 * of the message; beside the date it reads as a control over it, which is where the thread widget
 * has always put an item's actions.
 */
function Row({ item, self, tone, bubbleClass, actions, author, authorHref, children }) {
  const side = self ? 'playground-thread-row-self' : 'playground-thread-row-other';
  const bubble = self ? 'playground-thread-bubble-self' : 'playground-thread-bubble-other';
  const relative = item.created_at ? Utils.formatTimeAgo(item.created_at) : null;
  const absolute = item.created_at ? Utils.formatDateTime(item.created_at) : null;
  return (
    <div className={`playground-thread-row ${side} mc-tone-${tone || 'plain'}`}>
      <div className={`playground-thread-bubble ${bubble}${bubbleClass ? ` ${bubbleClass}` : ''}`}>
        {children}
      </div>
      <div className="playground-thread-meta">
        {/* Shown on EVERY item, unlike the thread widget's, which hides it on your own messages.
            Here the writer is the point: orchestrator, worker and human all write to these threads
            and an item means something different depending on which one did. */}
        {author && (
          <>
            {authorHref
              ? <a
                  className="playground-thread-meta-author playground-thread-meta-author-link"
                  href={authorHref}
                  target="_blank"
                  rel="noreferrer"
                >{author}</a>
              : <span className="playground-thread-meta-author">{author}</span>}
            <span className="playground-thread-meta-sep">&middot;</span>
          </>
        )}
        {relative && (
          <Tooltip text={absolute}>
            <span className="playground-thread-meta-time">{relative}</span>
          </Tooltip>
        )}
        {actions && (
          <>
            <span className="playground-thread-meta-sep">&middot;</span>
            <span className="playground-thread-actions">{actions}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** True when a body is long enough to be worth clamping. */
function isLong(text) {
  return typeof text === 'string' && text.length > CLAMP_AT;
}

/**
 * Every TRANSCRIPT item wears the same head: the type as itself, then the one machine fact the
 * prose does not already carry.
 *
 * The type is `metadata.type` verbatim. Mapping it through a table of English titles is what made
 * the first cut inconsistent — some items got a sentence, some a noun, some the name of a thing
 * that was then repeated word-for-word by the line underneath.
 */
function ItemHead({ shape }) {
  return (
    <div className="mc-head">
      <code className="mc-type">{shape.type}</code>
      {shape.qualifier && <span className="mc-qualifier">{shape.qualifier}</span>}
    </div>
  );
}

/**
 * A mark per record type — a SECOND signal over the type's own name, never a replacement for it.
 *
 * Five types is few enough that a glyph separates them at a glance, which is the whole argument for
 * having one: scrolling a cell's history you are looking for the two `output`s and the question, not
 * reading eleven words that say `info`. What the mark must not become is the table of English titles
 * this surface removed — so nothing is load-bearing here. A type that is not in this map renders
 * with a neutral mark and its own name, exactly as it did before there were marks.
 */
const RECORD_MARK = {
  info: <LuInfo />,
  output: <LuMessageSquare />,
  input_needed: <LuHelpCircle />,
  input_provided: <LuReply />,
  artifact: <LuFile />
};

function known(type) {
  return Object.prototype.hasOwnProperty.call(RECORD_MARK, type);
}

/**
 * `step` and `run`, in the ONE treatment they get anywhere on this surface.
 *
 * It reads `<step>.<run>` — the same string the object tree labels the run with and the session pane
 * heads itself with, so the chip in the feed and the row in the tree are visibly the one run. It was
 * prose before ("document (run 1)"), spliced into the same line as the event slug, which spent a
 * clause on two fields that are structure and left cell-level records rendering a phrase-shaped
 * blank. A record about no run has no chip at all.
 *
 * It opens that run when — and only when — the run has a session to open. A gate run opens none, and
 * a chip that navigates nowhere is worse than a chip that does not offer to.
 */
function RunTag({ run, onOpen }) {
  if (!run) return null;
  if (!onOpen) return <code className="mc-run">{run.key}</code>;
  return (
    <button type="button" className="mc-run-link" aria-label={`Open ${run.key}`} onClick={onOpen}>
      <code className="mc-run">{run.key}</code>
    </button>
  );
}

/**
 * Every META record wears the same head, and this is the whole of it:
 *
 *     [{mark} {type}]  {event}  {step}.{run}  [{label}]
 *
 * Four slots, in that order, for every type there is and every type there will be. The last two can
 * be absent — a record about the cell as a whole is about no run, and one that ended with nothing
 * worth naming has no label — but neither ever moves, and no type gets a slot of its own.
 *
 * The uniformity is the feature, not a tidiness preference. The previous cut put the run before the
 * verb, kept an `info`'s sentence ON this row while every other type's body went under it, and let
 * a question's `kind` occupy the slot a verb occupies elsewhere; the result was a feed where the
 * same three facts appeared in a different order and a different place depending on the row, and
 * nothing could be scanned down a column.
 *
 * The LABEL is the last of the four and it is a pill, whatever the type carried it: an agent's exit,
 * the option a person picked, the state a cell reached. Written as a word instead, an `info` read
 * `finished completed` — two bare words in a row with nothing to say which was the verb and which
 * was the value. Per-type detail trails the invariant part and stays in front of the pill; a
 * question's `kind` is the only one there is.
 */
function RecordHead({ shape, onOpenRun }) {
  return (
    <div className="mc-head">
      <code className={`mc-type${known(shape.type) ? ` mc-type-${shape.type}` : ''}`}>
        {RECORD_MARK[shape.type] || <LuCircle />}
        {shape.type}
      </code>
      {/* `metadata.event`, verbatim. No table maps a type to a verb here — the same argument that
          shows the type as itself shows its verb as itself, and a record that carries none says
          nothing rather than having a word chosen for it. */}
      {shape.event && <span className="mc-qualifier">{shape.event}</span>}
      <RunTag run={shape.run} onOpen={onOpenRun} />
      {shape.qualifier && <span className="mc-qualifier">{shape.qualifier}</span>}
      {shape.unrouted && <span className="mc-qualifier">no exit parsed</span>}
      {shape.label && (
        <Badge variant="secondary" className="title-tag-badge">{shape.label}</Badge>
      )}
    </div>
  );
}

/**
 * A body in the mode its type calls for — and never cut.
 *
 * A long body is clamped by HEIGHT and opens in place, so every character is present the whole
 * time. Slicing the string was the old behaviour and it lost the end of exactly the things worth
 * reading: a shell command's redirect, a file path's filename, a JSON argument's closing brace.
 */
function ItemBody({ body, expanded }) {
  if (!body || !body.text) return null;
  const clamp = !expanded && isLong(body.text) ? ' mc-clamp' : '';
  if (body.mode === 'markdown') {
    // `breaks` travels with the body because it is a fact about where the text CAME FROM — a person's
    // field rather than an agent's document — which only `describeRecord` knows.
    return <MarkdownBody text={body.text} breaks={body.breaks} className={`mc-markdown${clamp}`} />;
  }
  if (body.mode === 'code') {
    return <pre className={`mc-code${clamp}`}>{body.text}</pre>;
  }
  return <div className={`mc-body${clamp}`}>{body.text}</div>;
}

/**
 * The action bar every item carries: copy, expand when there is more to see, and the item itself as
 * JSON.
 *
 * The JSON panel replaced a metadata panel and a details table. All three showed pieces of one
 * record — and the two smaller ones showed fields (`id`, `user_id`) that the row already names or
 * that mean nothing without the rest. One panel, the whole item, matching exactly what copy puts on
 * the clipboard.
 */
function ItemActions({ item, expandable, expanded, onExpand, panel, onPanel }) {
  return (
    <>
      <ThreadItemAction tooltip="Copy to clipboard" confirmTooltip="Copied"
        icon={<LuCopy />} confirmIcon={<LuCheck />}
        onActivate={() => Clipboard.to(copyText(item))} />
      {expandable && (
        <ThreadItemAction tooltip={expanded ? 'Collapse' : 'Expand'}
          icon={expanded ? <LuChevronUp /> : <LuChevronDown />} onActivate={onExpand} />
      )}
      <ThreadItemAction tooltip={panel ? 'Hide item JSON' : 'Show item JSON'}
        icon={<CgDetailsMore />} onActivate={onPanel} />
    </>
  );
}

/** The panel the action bar opens, beneath the item's own body but inside its bubble. */
function ItemPanels({ item, panel }) {
  if (!panel) return null;
  return (
    <div className="playground-thread-metadata-block">
      <div className="playground-thread-metadata-label">item</div>
      <pre className="playground-thread-metadata-json">{copyText(item)}</pre>
    </div>
  );
}

/** The worker excerpts an item too large for the thread; a transcript with a hole should say so. */
function TruncationNote({ fields }) {
  if (!fields || fields.length === 0) return null;
  return <div className="mc-usage">excerpted by the worker: {fields.join(', ')}</div>;
}

/**
 * The scroll region, with the thread widget's contract:
 *
 *  - it opens at the BOTTOM, because the newest item is the one you came for;
 *  - scrolling within 40px of the top loads the previous page, the same trigger
 *    `playground/widgets/thread` uses;
 *  - loading older PRESERVES the reading position by restoring the scroll offset from the height
 *    delta. Without that, prepending a page yanks the view to a different message and the reader
 *    loses their place — the single thing that makes infinite scroll feel broken.
 *  - and it FOLLOWS a thread the reader is at the bottom of, which is what watching a running agent
 *    means. It used to follow by accident: the surface blanked the pane before every refetch, so
 *    every refresh arrived as a first load and scrolled itself down. Now that a refresh holds what
 *    is on screen, following is a decision, and it is the reader's position that decides — someone
 *    who scrolled up to read is left where they were.
 *
 * The button stays, for keyboards and for anyone who would rather ask than scroll.
 */
export class Thread extends Component {
  ref = React.createRef();

  // Until the reader scrolls, they are at the bottom: that is where `toBottom` opens the thread.
  atBottom = true;

  componentDidMount() {
    this.toBottom();
    // Bubbles take a share of the width until there is too little to share, then all of it. This
    // feed never did: the behaviour lived inside the thread widget's own view, so the meta-thread
    // — a different component on purpose — stayed at 85% however narrow the pane became, which at
    // a docked width is a column of text three words across with half the pane empty beside it.
    this.disposeNarrow = observeNarrow(this.ref.current);
  }

  componentWillUnmount() {
    if (this.disposeNarrow) this.disposeNarrow();
  }

  componentDidUpdate(previous) {
    if (previous.items !== this.props.items) {
      if (this.pendingHeight != null) {
        const element = this.ref.current;
        if (element) element.scrollTop = element.scrollHeight - this.pendingHeight;
        this.pendingHeight = null;
      } else if (previous.items == null || previous.anchor !== this.props.anchor || this.atBottom) {
        this.toBottom();
      }
    }
  }

  toBottom() {
    requestAnimationFrame(() => {
      const element = this.ref.current;
      if (element) element.scrollTop = element.scrollHeight;
    });
  }

  loadOlder = () => {
    const element = this.ref.current;
    this.pendingHeight = element ? element.scrollHeight : null;
    this.props.onLoadOlder();
  };

  handleScroll = (event) => {
    const element = event.target;
    // The same 40px either end: within it of the bottom is "following", within it of the top asks
    // for the previous page.
    this.atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
    if (element.scrollTop < 40 && this.props.onLoadOlder && !this.props.loadingOlder) {
      this.loadOlder();
    }
  };

  render() {
    const { onLoadOlder, loadingOlder, children } = this.props;
    return (
      <div className="mc-thread" ref={this.ref} onScroll={this.handleScroll}>
        {/* A status in the feed is a row in the feed, so it takes a row's gap under it (`mb-2`,
            8px — `.playground-thread-row`'s own margin) instead of sitting flush against the first
            item. `.playground-widget-status-alert` zeroes the alert's margin, and a Bootstrap
            spacing utility carries `!important`, so the utility is what wins. */}
        {loadingOlder && (
          <WidgetStatus variant="primary" loading className="mb-2">Loading older…</WidgetStatus>
        )}
        {onLoadOlder && !loadingOlder && (
          <div className="mc-load-older">
            <Button variant="light" size="sm" onClick={this.loadOlder}>Load older</Button>
          </div>
        )}
        {children}
      </div>
    );
  }
}

// -- the meta-thread ---------------------------------------------------------------------------

/** An artifact's facts, in whichever of the two the record actually carries. */
function artifactFacts(shape) {
  return [formatSize(shape.size), shape.contentType].filter(Boolean).join(' · ');
}

/**
 * One record of a cell's story: the uniform head, then whatever that type hangs beneath it.
 *
 * All five put their prose in the same place — a row of its own, under the head — and the two that
 * are prose people wrote go through the same markdown renderer:
 *
 *  - **`info`** is the orchestrator's bookkeeping, and the sentence it carries is a cell's title
 *    (`created`) or its closing summary (`finished`), so it renders as markdown under the head like
 *    an `output` does. It used to ride ON the head line, which is what made this feed's rows
 *    disagree about where a record's content lives. A feed is still mostly bookkeeping and still
 *    reads as one line per row — because a sentence that only restates the verb is dropped in
 *    `describeRecord`, not because this type is laid out differently from the rest.
 *  - **`output`** is what an agent wrote when it finished — long, and markdown as often as not. It
 *    is the reason anybody opened the cell, so it gets the full bubble and the run's token cost.
 *  - **`input_needed`** is a question, and renders through the one control that knows how to put
 *    one: live from the cell's open request, historical from the snapshot the record carries.
 *  - **`input_provided`** is the answer that unblocked it — short, and attributed by side.
 *  - **`artifact`** is a file, and renders as something to open rather than as a sentence about a
 *    file. Its prose (`path — size, type`) is the chip's own three facts written out, so printing
 *    both would say each of them twice.
 */
class Record extends Component {
  state = { expanded: false, panel: false };

  render() {
    const { item, onOpenArtifact, onOpenRun, openableRuns, client, onResolved, affordance,
            liveQuestionId, answered } = this.props;
    const shape = describeRecord(item);
    // A question is answerable exactly when it is the record the cell's OPEN request was raised on
    // (`openQuestionId`, computed once for the pane); every other one is history and renders without
    // controls. The record's own `request` snapshot cannot decide this — it is frozen at the moment
    // the question was put, and says nothing about whether anybody is still waiting on it.
    const open = (affordance && affordance.request) || null;
    const asking = shape.type === 'input_needed';
    const live = !!(asking && open && item.id && item.id === liveQuestionId);
    // A record's OWN snapshot comes first — it is the question as that record put it, and preferring
    // the cell's copy over it is how one record's question came to be drawn on another's row: while
    // a run's second question went unrecorded, the live request was the second question and the last
    // record was the first, and the feed showed them spliced.
    //
    // The cell's request is the fallback for a record that snapshotted none, and worth keeping for
    // one: prose alone is a question with no options, and a live record can borrow the options from
    // the request that is open on it. Historical, or with nothing open, the prose is all there is.
    const question = shape.request || (live ? open : null)
      || (shape.body ? { prompt: shape.body.text } : null);
    // A question's prose IS the question, and the control renders it; every other record's body
    // goes under the head, whatever its type. An `artifact` has none — its facts are the chip.
    const prose = !asking;
    const expandable = prose && !!(shape.body && isLong(shape.body.text));
    // The chip opens the run it names — when the run has a session to open. `openableRuns` is the
    // set the object tree actually has a node for, so this never offers a click it cannot deliver.
    const runKey = shape.run && shape.run.key;
    const openRun = runKey && onOpenRun && openableRuns && openableRuns.has(runKey)
      ? () => onOpenRun(runKey)
      : null;
    const actions = (
      <ItemActions item={item} expandable={expandable} expanded={this.state.expanded}
        onExpand={() => this.setState(state => ({ expanded: !state.expanded }))}
        panel={this.state.panel}
        onPanel={() => this.setState(state => ({ panel: !state.panel }))} />
    );
    return (
      <Row item={item} self={shape.isSelf} tone={shape.tone} actions={actions}
        author={client && client.authorName(item.user_id, onResolved)}
        authorHref={client && client.authorProfilePath && client.authorProfilePath(item.user_id)}>
        <RecordHead shape={shape} onOpenRun={openRun} />
        {shape.type === 'artifact' && (
          <Button variant="light" size="sm" className="mc-chip"
            onClick={() => onOpenArtifact && onOpenArtifact(shape.alias)}>
            <code>{shape.artifactPath}</code>
            {artifactFacts(shape) && (
              <span className="text-muted ml-2">{artifactFacts(shape)}</span>
            )}
          </Button>
        )}
        {asking && (
          <InputControl request={question} answered={answered} live={live}
            mode={affordance && affordance.mode}
            draft={live ? this.props.draft : null} busy={this.props.busy} />
        )}
        {prose && <ItemBody body={shape.body} expanded={this.state.expanded} />}
        {shape.usage && <div className="mc-usage">{formatUsage(shape.usage)}</div>}
        <ItemPanels item={item} panel={this.state.panel} />
      </Row>
    );
  }
}

/** The verb of the newest `intent` a person posted, or null. */
function lastIntent(items) {
  for (let index = (items || []).length - 1; index >= 0; index -= 1) {
    const metadata = (items[index] || {}).metadata || {};
    if (metadata.type === 'intent') return metadata.event || null;
  }
  return null;
}


export function MetaPane({ canEdit, items, loading, loadingOlder, onLoadOlder, cell, affordance,
                           onIntend, intendBusy, intendError, onOpenArtifact,
                           onOpenRun, runKeys, client, onResolved }) {
  const status = (cell && cell.props && cell.props.status) || null;
  // WHAT THE CELL IS ASKING — the one thing the input control at the foot reads. Published in every
  // status that takes input, so there is no table here saying which of them do.
  const request = (cell && cell.props && cell.props.request) || null;
  // ONE answer, two views of it: the rows are drawn on the record that raised the question, in the
  // thread that scrolls, and the button that commits them is pinned at the foot. So the draft is
  // held here, above both, rather than inside either.
  const draft = useAnswerDraft(request);
  // Which record, if any, carries the question somebody may answer right now. Read-only makes that
  // "none", which is the whole of read-only here: the open question is still shown, with its
  // options and its prose, in exactly the rendering it will have once it HAS been answered. That is
  // the honest read-only view of a question — not a hidden one, and not a dead Send.
  //
  // `affordance` itself is left alone deliberately: it also carries `mode`, which decides whether a
  // record reads as a gate or an in-flight prompt. Nulling it to block the control would have
  // changed how answered gates render, which is a read.
  // What each ANSWERED question was answered with, so a question that is history still reads as the
  // question it was — the same options, with the chosen ones marked — rather than as a wall of the
  // prose the worker happened to render. Read first, because whether a question is still live
  // depends on it.
  const answered = answersByQuestion(items);
  const openId = openQuestionId(items, affordance && affordance.request);
  // …and a question that HAS been answered is not it, whatever the cell still says.
  //
  // The cell's `input_needed` clears when the ORCHESTRATOR next ticks, which is up to a minute
  // after somebody answers. Until then `openQuestionId` still names this record — it matches on
  // step, run and prompt id, none of which an answer changes — so the record went on wearing its
  // full controls, Submit row and all, over a question already answered. The `input_provided`
  // record on the thread is the fact that settles it, and it is on the thread, so a refresh in that
  // window renders the same thing rather than reopening the question.
  const liveQuestionId = canEdit === false || (openId && answered[openId]) ? null : openId;
  // **Asked for, and not yet picked up.** The cell keeps its status until the orchestrator next
  // ticks, up to a minute later, so its request goes on asking — and the foot went on offering the
  // same send it had just taken. A second one is at best ignored (`route._earliest_answer` consumes
  // the first) and at worst refused, so the control was an invitation to post something nothing
  // would act on.
  //
  // Read off the REQUEST a person made rather than off the status, and off every request rather
  // than only an answer: `complete` ends the cell from a question and is not an answer to it, so a
  // rule that counted answers left the control up under an ending somebody had already asked for.
  //
  // `message` is the exception and the only one: saying something to a running cell changes nothing
  // and can be done again immediately, so a composer that vanished for a minute after every line
  // would make steering unusable. `intent_pending` is the orchestrator's own "posted, not yet
  // applied" bit, cleared on the reconcile that reads it.
  const asking = lastIntent(items);
  const settled = !!(openId && answered[openId])
    || (!!(cell && cell.props && cell.props.intent_pending) && asking && asking !== 'message');
  // Which run chips are worth clicking: the ones the tree has a node for. A gate run opens no
  // session, so its records name a run that cannot be drilled into, and the chip says so by being
  // inert rather than by failing when pressed.
  const openable = new Set(runKeys || []);
  return (
    <div className="mc-pane">
      {/* The same head shape the session pane carries, so the two panes read as siblings rather
          than as two unrelated screens: what this thread IS, then what state it is in. */}
      <div className="mc-pane-head">
        {/* Named exactly as the tree names it, for the reason the session pane's head is. */}
        <h6 className="mb-0"><code>meta</code> Thread</h6>
        {status && <span className="mc-pane-state">cell is <StatusPill status={status} /></span>}
      </div>
      <Thread items={items} onLoadOlder={onLoadOlder} loadingOlder={loadingOlder}>
        {loading && (
          <WidgetStatus variant="primary" loading className="mb-2">Loading activity…</WidgetStatus>
        )}
        {/* A draft cell has no meta-thread OBJECT yet — the orchestrator creates it when the cell is
            activated (see client.addCell) — so this is the ordinary state of a cell that has not
            run, not a failed read. `pageMetaThread` returns an empty page for an absent object
            rather than throwing, and the words are the rail's and the object tree's own. */}
        {!loading && (!items || items.length === 0) && (
          <WidgetStatus variant="light">No activity yet.</WidgetStatus>
        )}
        {(items || []).map((item, index) => (
          <Record key={item.id || index} item={item} onOpenArtifact={onOpenArtifact}
            onOpenRun={onOpenRun} openableRuns={openable}
            client={client} onResolved={onResolved} affordance={affordance}
            liveQuestionId={liveQuestionId} answered={answered[item.id]}
            draft={draft} busy={intendBusy} />
        ))}
      </Thread>
      {/* ONE place a person says anything to this cell — a question the agent asked, a word to the
          run, a resume, a restart. What varies is the request the cell publishes, and the control
          reads it: its questions are the fields, its intents are the button. A cell asking nothing
          renders nothing. Read-only takes it away with everything else that writes.

          `carried` is `openId`, NOT `liveQuestionId`: the question is carried by a record on screen
          whether or not it is still answerable. The pane nulls `liveQuestionId` for two other
          reasons — the surface is read-only, and the question has just been answered — and on the
          second one the foot started drawing the question over the field again, un-bubbled, for the
          up-to-a-minute it takes the orchestrator to clear `input_needed`. */}
      {canEdit !== false && onIntend && !settled && (
        <>
          {intendError && <WidgetStatus variant="warning">{intendError}</WidgetStatus>}
          <CellInput draft={draft} busy={intendBusy} onSend={onIntend}
            carried={openId !== null} />
        </>
      )}
    </div>
  );
}

// -- one run's transcript ----------------------------------------------------------------------

/** What a transcript item cost or produced — everything under its body. */
function TranscriptFooter({ shape }) {
  return (
    <>
      {shape.stats && <div className="mc-usage">{formatUsage(shape.stats)}</div>}
      {shape.error && <div className="mc-usage">{shape.error}</div>}
      {shape.aliases && shape.aliases.length > 0 && (
        <div className="mc-usage">
          {shape.aliases.map(alias => alias.split('/artifacts/')[1] || alias).join(', ')}
        </div>
      )}
      {shape.rules && shape.rules.length > 0 && (
        <div className="mc-usage">
          {shape.rules.map(rule => rule.id).join(', ')}
          {shape.policyDefault ? ` · default ${shape.policyDefault}` : ''}
        </div>
      )}
      <TruncationNote fields={shape.truncated} />
    </>
  );
}


/**
 * A run's transcript, READ-ONLY — and it stays that way now that answering works.
 *
 * The one write this widget makes to a session thread is an answer to an open question, and it is
 * made from that question's own record in the timeline. Putting a composer here as well would put a
 * text box beside a parked agent, and the worker resolves ANY inbound text against a pending prompt
 * before treating it as input: a note typed here as steering would silently become the answer to a
 * question the writer never read. The transcript is what the agent said; the cell's controls are how
 * it is answered.
 */
export class SessionPane extends Component {
  /**
   * Where this pane's bytes come from. Four reads and two name lookups — no post, which is what
   * makes the view above read-only by construction rather than by a flag somebody can flip.
   *
   * Rebuilt per render and keyed by the run's alias, which is what tells the view a DIFFERENT run
   * was selected from the same one gaining an item.
   */
  source() {
    const { session, client, onResolved } = this.props;
    return {
      key: session.alias,
      listOlder: ({ cursor }) => client.pageSessionThread(session.alias, cursor),
      listNewer: ({ since }) => client.listThreadItemsSince(session.alias, since),
      watch: (callback) => client.watchThread(session.alias, callback),
      unwatch: (callback) => client.unwatchThread(session.alias, callback),
      authorName: (userId) => client.authorName(userId, onResolved),
      authorProfilePath: (userId) => client.authorProfilePath && client.authorProfilePath(userId)
    };
  }

  // The same {api, revisionId, sessionId} the client resolves a session thread's items against
  // (see pageSessionThread / _pageThread) — file blocks fetch their bytes from the same place.
  // Guarded because `client` is not always a live MissionsClient — tests substitute a narrower
  // fake exposing only its public read/write surface — and a client without these internals
  // should leave file blocks with no fileContext rather than crash the pane.
  fileContext() {
    return fileContextFor(this.props.client);
  }

  render() {
    const { session, state } = this.props;
    // Two different facts, and both are worth the space: `worker state` is `instance.state`, the
    // worker's own lifecycle — is anything still running — while `exited` is the outgoing edge the
    // run took. A run can be `completed` and have exited on `changes`, so neither implies the other.
    //
    // Except when the exit is `ok`, which is not a decision: the orchestrator synthesises it for a
    // step with nothing to choose between, so it lands on almost every run and pairs a pill saying
    // nothing with a pill that already said the run finished.
    const exit = isSingleExitLabel(session.label) ? null : session.label;
    return (
      <div className="mc-pane">
        <div className="mc-pane-head">
          {/* Named exactly as the tree names it, so the row you clicked and the pane you are
              looking at agree. */}
          <h6 className="mb-0"><code>{session.key}</code> Thread</h6>
          <span className="mc-pane-state">
            {state ? <>worker state <StatusPill status={state} /></> : 'not started'}
            {exit && (
              <> · exited <Badge variant="secondary" className="title-tag-badge">{exit}</Badge></>
            )}
          </span>
        </div>
        {/* THE THREAD WIDGET'S OWN VIEW (common/threadView.js) — loading, paging, the live tail,
            the scroll behaviour and the item rendering, all of it. This pane is a convenience for
            reading a run while debugging it, and a second implementation of "show me a thread" is
            two sets of scroll bugs and an improvement that reaches half the product.
            READ-ONLY, by having no way to be otherwise: the view cannot post, and the source below
            offers nothing that could. A composer here would be worse than redundant — the worker
            resolves ANY inbound text against a pending prompt, so a note typed beside a parked agent
            would silently become the answer to a question its writer never read. Answering is the
            meta-thread's, which stays this widget's own. */}
        <ThreadView
          source={this.source()}
          className="mc-thread-scroll"
          contentView="markdown"
          currentUserId={null}
          emptyMessage="No transcript yet — the worker has not picked this run up."
          // What a worker's item MEANS is this widget's knowledge, not the view's: the type and the
          // tool it called, the body — which for a `tool_call` is the command it RAN, not the
          // truncated preview the worker wrote into `content` — and what the call cost or produced.
          renderHead={(item) => <ItemHead shape={describeItem(item)} />}
          renderContent={(item, contentView, expanded) => (
            <ItemBody body={describeItem(item).body} expanded={expanded} />
          )}
          renderFooter={(item) => <TranscriptFooter shape={describeItem(item)} />}
          isLong={(item) => {
            const body = describeItem(item).body;
            return !!(body && isLong(body.text));
          }}
          fileContext={this.fileContext()}
        />
      </div>
    );
  }
}

// -- an artifact -------------------------------------------------------------------------------

/** Extensions we render as something other than source, and what each becomes. */
const MARKDOWN = /\.(md|markdown)$/i;
const HTML = /\.html?$/i;

/**
 * Which Ace mode to highlight an artifact's SOURCE in, by extension.
 *
 * Source is the other half of every preview: the raw view exists for when the text itself is the
 * point — a diff to check, a script to read before trusting it, a fence to copy — and unhighlighted
 * it is the same wall of characters whatever the file is. Keyed on extension rather than on the
 * stored `content_type`, because the worker publishes whatever the OS guessed and an agent's `.html`
 * has arrived as `text/plain` more than once.
 *
 * `text` for anything unlisted: a mode that guesses wrong colours the file misleadingly, which is
 * worse than not colouring it.
 */
const ACE_MODES = [
  [/\.html?$/i, 'html'],
  [/\.(md|markdown)$/i, 'markdown'],
  [/\.json$/i, 'json'],
  [/\.(js|jsx|mjs|cjs)$/i, 'javascript'],
  [/\.(ya?ml)$/i, 'yaml'],
  [/\.py$/i, 'python'],
  [/\.css$/i, 'css'],
  [/\.(sh|bash|zsh)$/i, 'sh'],
  [/\.(diff|patch)$/i, 'diff'],
  [/\.sql$/i, 'sql'],
  [/\.xml$/i, 'xml'],
];

export function aceMode(path) {
  const match = ACE_MODES.find(([pattern]) => pattern.test(path || ''));
  return match ? match[1] : 'text';
}

/**
 * How to show this artifact when it is not being shown as source.
 *
 * A plan or a report is written to be READ, and a page is written to be LOOKED AT; showing either as
 * source is showing the wrong thing. Source stays one click away, for when it is the point — a diff
 * to check, a fence to copy, a script to inspect before trusting it.
 */
function previewKind(path, contentType) {
  if (MARKDOWN.test(path) || contentType === 'text/markdown') return 'markdown';
  if (HTML.test(path) || contentType === 'text/html') return 'html';
  return null;
}

const PDF = /\.pdf$/i;

/**
 * What to render a `file` artifact AS, given its path and stored content_type. Everything the
 * browser can show inline is shown; everything else falls to a download chip.
 *
 *  - `markdown` / `html` — a document or a page, rendered, with source one click away (see previewKind)
 *  - `image`             — a raster image, through fileBlocks' audited allowlist (never SVG — see there)
 *  - `pdf`               — the browser's own PDF viewer
 *  - `text`              — any other `text/*`, shown as source
 *  - `file`              — anything else: bytes we will not render, offered as a download
 */
export function renderKind(path, contentType) {
  const preview = previewKind(path, contentType);
  if (preview) return preview;
  if (isImageBlock({ file: { content_type: contentType } })) return 'image';
  if (PDF.test(path) || contentType === 'application/pdf') return 'pdf';
  // Source files: a `text/*` content_type, or a path whose extension aceMode recognises — the worker
  // often mis-declares these (`.py` has no registered type, `.json` reports `application/json`), so
  // the extension is the more reliable signal for "show this as source" than the content_type alone.
  if (('string' === typeof contentType && contentType.startsWith('text/')) || aceMode(path) !== 'text') {
    return 'text';
  }
  return 'file';
}

/** A synthetic `file` content block, so an artifact object can reuse fileBlocks' image and chip
 *  components. `name` is the artifact's full path (its alias tail), which reads better than the
 *  stored basename as an <img> alt or a chip label. */
function artifactBlock(artifact, meta) {
  return { type: 'file', file: { alias: artifact.alias, name: artifact.path,
                                 content_type: meta.content_type, size: meta.size } };
}

// Shared with Task 2, which will feed the pane a persisted value from outside.
export const ARTIFACT_ZOOM_MIN = 0.5;
export const ARTIFACT_ZOOM_MAX = 3;
export const ARTIFACT_ZOOM_STEP = 0.05;
export const ARTIFACT_ZOOM_DEFAULT = 1;

// The one clamp for this value — finite AND in range, falling back to the default otherwise.
// Shared by ArtifactPane (below) and CellDetailSurface's initialArtifactZoom (cell/index.js), so a
// corrupt or out-of-range stored value is rejected the same way regardless of which side reads it.
export function normalizeZoom(zoom) {
  return Number.isFinite(zoom) && zoom >= ARTIFACT_ZOOM_MIN && zoom <= ARTIFACT_ZOOM_MAX
    ? zoom : ARTIFACT_ZOOM_DEFAULT;
}

export class ArtifactPane extends Component {
  state = { raw: false, fetchedText: null, fetchError: false, pdfUrl: null, downgradeToDownload: false };

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentDidUpdate(prev) {
    // Re-fetch only when a different artifact is actually selected. `value` is held in the cell
    // surface's own state and changes only on selection, so it is the stable trigger. The `artifact`
    // node object is NOT: findNode/buildCellTree re-allocate it on every parent re-render (which the
    // mission store drives several times a minute), so keying on its identity would tear down and
    // re-download the artifact on a timer.
    if (prev.value !== this.props.value) {
      this.revokePdf();
      this.setState({ raw: false, fetchedText: null, fetchError: false, pdfUrl: null,
                      downgradeToDownload: false }, () => this.load());
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.revokePdf();
  }

  revokePdf() {
    if (this.state.pdfUrl) URL.revokeObjectURL(this.state.pdfUrl);
  }

  // Purely controlled: CellDetailSurface (the only caller) always passes both `zoom` and
  // `onZoomChange`, so this reads the value from props and hands every change straight back rather
  // than tracking a copy of its own. Without a callback the slider still renders — scaled from
  // whatever `zoom` prop it was given — but is inert: nothing is listening to move it.
  handleZoomChange(zoom) {
    if (this.props.onZoomChange) this.props.onZoomChange(zoom);
  }

  // A `file` artifact's bytes live outside the object, so the renders that need them fetch them
  // here: text (markdown / html / other text, decoded to a string) and pdf (a pdf-typed object URL
  // for the viewer frame). Images and plain downloads are left to fileBlocks' own components, which
  // fetch through the same cache on mount / click. A legacy `message` artifact needs no fetch — its
  // body is inline.
  load() {
    const { value, artifact, fileContext } = this.props;
    if (!isFileArtifact(value)) return;
    const kind = renderKind(artifact.path, artifactMeta(value).content_type);
    if ('image' === kind || 'file' === kind) return;
    if (!fileContext || !fileContext.api) { this.setState({ fetchError: true }); return; }
    fileContext.api.sessionFileDownload(fileContext.revisionId, fileContext.sessionId, artifact.alias)
      .then((response) => {
        // Ignore a fetch that a newer selection has superseded — the pane is still mounted but now
        // showing a different artifact, and this response would overwrite it.
        if (!this.mounted || this.props.value !== value) return;
        const binary = response && response.binary;
        // `binary` is filled only for an octet-stream response; anything else is a failure here, not
        // an empty artifact — decoding `undefined` would render silently blank.
        if (!binary) { this.setState({ fetchError: true }); return; }
        if ('pdf' === kind) {
          // The pdf frame is unsandboxed and both `content_type` and the path are worker-controlled,
          // so only bytes that actually begin with the `%PDF-` signature reach the viewer; anything
          // else falls to a download — the same conservatism fileBlocks applies to image types.
          const head = new Uint8Array(binary.slice(0, 5));
          const isPdf = head.length === 5 && 0x25 === head[0] && 0x50 === head[1]
            && 0x44 === head[2] && 0x46 === head[3] && 0x2d === head[4];
          if (isPdf) {
            this.setState({ pdfUrl: URL.createObjectURL(new Blob([binary], { type: 'application/pdf' })) });
          } else {
            this.setState({ downgradeToDownload: true });
          }
        } else {
          this.setState({ fetchedText: new TextDecoder().decode(binary) });
        }
      }, () => {
        if (this.mounted && this.props.value === value) this.setState({ fetchError: true });
      });
  }

  render() {
    const { artifact, value, fileContext } = this.props;
    const isFile = isFileArtifact(value);
    const meta = artifactMeta(value);
    const path = meta.filename || artifact.path;
    // A legacy `message` artifact only ever held inline text, so it is always a document; a `file`
    // artifact takes the full matrix — image, pdf and download included.
    const kind = isFile ? renderKind(path, meta.content_type)
                        : (previewKind(path, meta.content_type) || 'text');
    const textual = 'markdown' === kind || 'html' === kind || 'text' === kind;
    // Markdown/html render by default, with source one click away; the toggle only exists for them.
    const toggleable = 'markdown' === kind || 'html' === kind;
    const preview = toggleable && !this.state.raw;
    // The body text is inline for a legacy message and fetched for a file object.
    const text = isFile ? this.state.fetchedText : artifactText(value);
    const awaiting = isFile && !this.state.fetchError && !this.state.downgradeToDownload
      && ((textual && null === text) || ('pdf' === kind && null === this.state.pdfUrl));
    const loading = null === value || awaiting;
    const block = isFile ? artifactBlock(artifact, meta) : null;
    const zoom = normalizeZoom(this.props.zoom);
    // The same gate the markdown render itself is under (below) — a scale control for a document
    // that is not currently on screen (Raw, a different kind, still loading, or empty) has nothing
    // to act on.
    const showZoom = 'markdown' === kind && preview && !loading && !this.state.fetchError
      && !this.state.downgradeToDownload && !!text;
    return (
      <div className="mc-pane">
        {/* Three slots, so the file's facts sit in the same place whether or not this artifact has
            a view toggle — the head reads identically for a .md and a .txt. */}
        <div className="mc-pane-head mc-pane-head-centered">
          <h6 className="mb-0 bh-alias-fit">{path}</h6>
          <span className="mc-pane-state">
            {meta.content_type || 'unknown'}
            {meta.size !== undefined && <> · {formatSize(meta.size)}</>}
            {artifact.session && <> · from <code>{artifact.session.key}</code></>}
          </span>
          <div className="mc-pane-tools">
            {showZoom && (
              <div className="d-inline-flex align-items-center mr-3">
                <input
                  type="range"
                  className="bh-zoom-slider"
                  min={ARTIFACT_ZOOM_MIN}
                  max={ARTIFACT_ZOOM_MAX}
                  step={ARTIFACT_ZOOM_STEP}
                  value={zoom}
                  aria-label="Content zoom"
                  aria-valuetext={`${Math.round(zoom * 100)}%`}
                  onChange={(e) => this.handleZoomChange(parseFloat(e.target.value))}
                />
                <span className="bh-zoom-readout text-muted small ml-1 text-right">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
            )}
            {toggleable && !loading && (
              <ButtonGroup toggle className="mc-pane-toggle bh-view-toggle">
                <ToggleButton type="radio" variant="light" checked={!this.state.raw}
                  value="preview" onChange={() => this.setState({ raw: false })}>
                  {kind === 'html' ? 'Preview' : 'Markdown'}
                </ToggleButton>
                <ToggleButton type="radio" variant="light" checked={this.state.raw}
                  value="raw" onChange={() => this.setState({ raw: true })}>Raw</ToggleButton>
              </ButtonGroup>
            )}
          </div>
        </div>
        {loading && <WidgetStatus variant="primary" loading>Loading…</WidgetStatus>}
        {!loading && this.state.fetchError && (
          <WidgetStatus variant="light">Could not load this artifact.</WidgetStatus>
        )}
        {!loading && !this.state.fetchError && !this.state.downgradeToDownload && 'image' === kind && (
          <div className="mc-artifact-doc"><ThreadImage block={block} fileContext={fileContext} /></div>
        )}
        {/* The browser's own PDF viewer, reached only once the bytes have been confirmed to be a PDF
            (see load()). Unlike the html render below, a pdf frame CANNOT be sandboxed: a sandboxed
            frame's opaque origin cannot read a blob: URL this origin created, so it would load
            nothing. The bytes are the user's own run's and the native viewer, not this page, is the
            renderer — the same trust boundary a browser applies to any local PDF. */}
        {!loading && !this.state.fetchError && !this.state.downgradeToDownload && 'pdf' === kind && (
          <iframe className="mc-artifact-frame" title={path} src={this.state.pdfUrl} />
        )}
        {!loading && !this.state.fetchError && (this.state.downgradeToDownload || 'file' === kind) && (
          <div className="mc-artifact-doc"><FileChip block={block} fileContext={fileContext} /></div>
        )}
        {/* `zoom` scales its OWN element's layout box, not just its content (see grid.css:132-142
            for the same lesson on the widget wrapper) — zooming `.mc-artifact-doc` itself would
            grow the scroll container along with the text, so the scaled box is an unzoomed child
            instead and the scroller's own size stays put. */}
        {!loading && !this.state.fetchError && textual && text && preview && 'markdown' === kind && (
          <div className="mc-artifact-doc"><div style={{ zoom }}><MarkdownBody text={text} /></div></div>
        )}
        {/* An artifact is a file an AGENT wrote, so its page is rendered at arm's length: sandboxed
            with `allow-scripts` and NEVER `allow-same-origin` — the two together are no sandbox at
            all, since the frame could then reach back into this document and its storage. Opaque
            origin, no referrer, no navigation of the top frame. Same posture as the html widget. */}
        {!loading && !this.state.fetchError && textual && text && preview && 'html' === kind && (
          <iframe
            className="mc-artifact-frame"
            title={path}
            sandbox="allow-scripts"
            srcDoc={text}
            referrerPolicy="no-referrer"
          />
        )}
        {!loading && !this.state.fetchError && textual && text && !preview && (
          <div className="mc-artifact-source">
            <AceView
              name="mc-artifact-source"
              width="100%"
              height="100%"
              mode={aceMode(path)}
              readOnly
              wrapEnabled
              showPrintMargin={false}
              value={text}
              setOptions={{ useWorker: false, highlightActiveLine: false, showGutter: true }}
            />
          </div>
        )}
        {!loading && !this.state.fetchError && textual && !text && (
          <WidgetStatus variant="light">This artifact is empty.</WidgetStatus>
        )}
      </div>
    );
  }
}
